import { useId } from 'react'
import { AVATAR_CATALOG, type AvatarConfig, type AvatarField } from './avatar-catalog'
import { DonorAvatar } from './DonorAvatar'
import styles from './AvatarEditor.module.css'
import { equipAvatarPiece, removeAvatarPiece, type AvatarPiece } from './avatar-custom'

export type AvatarChange = (next: AvatarConfig) => void

type Props = {
  field: AvatarField
  label: string
  config: AvatarConfig
  onChange: AvatarChange
  colors?: boolean
  campaignItems?: AvatarPiece[]
  onBuy?: (piece: AvatarPiece) => void
}

export function AvatarOptions({
  field,
  label,
  config,
  onChange,
  colors = false,
  campaignItems = [],
  onBuy,
}: Props) {
  const id = useId()
  const options = AVATAR_CATALOG[field]
  const equipped = config.custom?.find((piece) => piece.category === field)
  const currentLabel =
    equipped?.name ?? options.find(([value]) => value === config[field])?.[1]
  const exclusive = campaignItems
    .filter((piece) => piece.category === field)
    .map((piece) => (piece.id === equipped?.id ? equipped : piece))
  if (equipped && !exclusive.some((piece) => piece.id === equipped.id))
    exclusive.unshift(equipped)
  return (
    <fieldset className={styles.optionGroup}>
      <legend>
        {label}
        <span>{currentLabel}</span>
      </legend>
      <div className={colors ? styles.swatches : styles.optionGrid}>
        {options.map(([value, name]) => {
          const selected = !equipped && config[field] === value
          const next = { ...removeAvatarPiece(config, field), [field]: value }
          // Headwear can hide hair; show the actual hairstyle in its own thumbnail.
          const preview = field === 'hair' ? { ...next, headwear: 'none' as const } : next
          return (
            <label
              key={value}
              className={colors ? styles.swatch : styles.option}
              title={name}
            >
              <input
                type="radio"
                name={id}
                value={value}
                checked={selected}
                aria-label={`${label}: ${name}`}
                onChange={() => onChange(next)}
              />
              {colors ? (
                <span className={styles.swatchColor} style={{ backgroundColor: value }}>
                  {selected && <span className={styles.colorCheck}>✓</span>}
                </span>
              ) : (
                <span className={styles.optionCard}>
                  <DonorAvatar
                    config={preview}
                    portrait={!['body', 'clothing'].includes(field)}
                  />
                  <span className={styles.optionName}>{name}</span>
                  {selected && (
                    <span className={styles.optionCheck} aria-hidden>
                      ✓
                    </span>
                  )}
                </span>
              )}
            </label>
          )
        })}
      </div>
      {exclusive.length > 0 && (
        <section
          className={styles.premiumCollection}
          aria-label={`${label}: coleção premium da campanha`}
        >
          <div className={styles.premiumHeading}>
            <span className={styles.premiumEmblem} aria-hidden>
              ✦
            </span>
            <div>
              <span className={styles.premiumEyebrow}>COLEÇÃO PREMIUM</span>
              <h3>Peças globais, da campanha e sua coleção</h3>
              <p>Peças especiais para vestir a sua causa.</p>
            </div>
          </div>
          <div className={styles.optionGrid}>
            {exclusive.map((piece) => {
              const next = equipAvatarPiece(config, piece)
              const locked =
                (piece.price_cents ?? 0) > 0 && !piece.owned && equipped?.id !== piece.id
              if (locked)
                return (
                  <button
                    type="button"
                    key={piece.id}
                    className={`${styles.option} ${styles.premiumOption}`}
                    aria-label={`Comprar ${piece.name}`}
                    onClick={() => onBuy?.(piece)}
                    disabled={!onBuy}
                  >
                    <span className={styles.optionCard}>
                      <span className={styles.premiumMark} aria-hidden>
                        ✦
                      </span>
                      <DonorAvatar
                        config={next}
                        portrait={!['body', 'clothing', 'background'].includes(field)}
                      />
                      <span className={styles.optionName}>{piece.name}</span>
                      <span className={styles.optionName}>
                        {((piece.price_cents ?? 0) / 100).toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        })}{' '}
                        · Comprar
                      </span>
                    </span>
                  </button>
                )
              return (
                <label
                  key={piece.id}
                  className={`${styles.option} ${styles.premiumOption}`}
                >
                  <input
                    type="radio"
                    name={id}
                    checked={equipped?.id === piece.id}
                    aria-label={`${label}: ${piece.name}`}
                    onChange={() => onChange(next)}
                  />
                  <span className={styles.optionCard}>
                    <span className={styles.premiumMark} aria-hidden>
                      ✦
                    </span>
                    <DonorAvatar
                      config={next}
                      portrait={!['body', 'clothing', 'background'].includes(field)}
                    />
                    <span className={styles.optionName}>{piece.name}</span>
                    <span className={styles.optionName}>
                      {piece.owned
                        ? 'Adquirida · usar'
                        : (piece.price_cents ?? 0) > 0
                          ? 'Em uso'
                          : 'Gratuita'}
                    </span>
                    {equipped?.id === piece.id && (
                      <span className={styles.optionCheck} aria-hidden>
                        ✓
                      </span>
                    )}
                  </span>
                </label>
              )
            })}
          </div>
        </section>
      )}
    </fieldset>
  )
}
